<template>
  <div class="modal-overlay">
    <div class="modal">

      <transition name="fade">
        <div v-if="isSuccess" class="success-overlay">
          <div class="success-box">
            <div class="success-icon">✓</div>
            <div class="success-title">
              Готово!
            </div>
            <div class="success-subtitle">
              AI успешно обработал ваше резюме
            </div>
            <button class="success-btn" @click="handleSuccessContinue">
              Продолжить
            </button>
          </div>
        </div>
      </transition>

      <div class="header">
        <div class="icon">✨</div>
        <h2>AI Resume Assistant</h2>
        <button class="close" @click="$emit('close')">✕</button>
      </div>

      <p class="subtitle">
        Загрузите PDF или DOC файл — AI оформит ваше резюме автоматически
      </p>

      <!-- Upload -->
      <div 
        class="upload-zone"
        :class="{ active: isDragging }"
        @click="fileInput.click()"
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <input
          type="file"
          ref="fileInput"
          accept=".pdf,.doc,.docx"
          hidden
          @change="handleFile"
        />

        <template v-if="!file">
          <div class="upload-icon">📄</div>
          <div class="upload-title">Перетащи файл</div>
          <div class="upload-sub">или нажми для выбора</div>
        </template>

        <template v-else>
          <div class="file-chip">
            <span class="file-icon">
              📄
            </span>

            <span class="file-name">
              {{ file.name }}
            </span>

            <button
              type="button"
              class="file-remove"
              aria-label="Удалить файл"
              @click.stop="file = null"
            >
              ✕
            </button>
          </div>
        </template>
      </div>

      <!-- Button -->
      <button 
        class="run-btn"
        :disabled="!file || isLoading"
        @click="handleRun"
      >
        <span v-if="!isLoading">⚡ Выполнить</span>
        <span v-else class="loader"></span>
      </button>

    </div>
  </div>
</template>

<script setup>

import { ref } from 'vue';
import PremiumService from "../api/PremiumService";

const file = ref(null);
const isLoading = ref(false);
const isDragging = ref(false);
const fileInput = ref(null);
const isSuccess = ref(false);

function handleFile(e) {
  file.value = e.target.files[0];
}

function handleDrop(e) {
  isDragging.value = false;
  file.value = e.dataTransfer.files[0];
}

const emit = defineEmits([
  "close",
  "completed"
]);

async function handleRun() {
  if (!file.value) return;

  isLoading.value = true;

  try {
    const result = await PremiumService.aiResumeParse(file.value);

    console.log(result);
    isSuccess.value = true;
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
}

function handleSuccessContinue() {
  isSuccess.value = false;
  file.value = null;

  emit("completed");
}

</script>


<style scoped>

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal {
  width: 440px;
  padding: 28px;
  border-radius: 24px;

  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(20px);

  box-shadow:
    0 10px 40px rgba(0,0,0,0.2),
    inset 0 1px rgba(255,255,255,0.6);

  position: relative;
}

[data-theme="dark"] .modal {
  background: rgba(30,30,30,0.9);
  box-shadow:
    0 10px 40px rgba(0,0,0,0.6),
    inset 0 1px rgba(255,255,255,0.05);
}

/* HEADER */

.header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header h2 {
  font-size: 18px;
  font-family: "Eastman-Bold";
}

.icon {
  font-size: 20px;
}

/* subtitle */

.subtitle {
  margin-top: 8px;
  font-size: 13px;
  opacity: 0.7;
}

/* upload */

.upload-zone {
  margin-top: 20px;
  padding: 30px;
  border-radius: 18px;
  text-align: center;
  cursor: pointer;

  border: 2px dashed #ddd;
  transition: 0.25s;
}

.upload-zone:hover {
  border-color: #2887F7;
  background: rgba(40,135,247,0.05);
}

.upload-zone.active {
  border-color: #2887F7;
  background: rgba(40,135,247,0.1);
}

.upload-icon {
  font-size: 28px;
}

.upload-title {
  font-weight: 600;
  margin-top: 6px;
}

.upload-sub {
  font-size: 12px;
  opacity: 0.6;
}

/* file chip */

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  padding: 8px 14px;
  border-radius: 999px;

  background: #2887F7;
  color: white;
  font-size: 13px;
}

.file-chip button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
}

/* button */

.run-btn {
  margin-top: 20px;
  width: 100%;
  height: 48px;

  border-radius: 14px;
  border: none;

  font-family: "Eastman-Bold";
  font-size: 14px;

  color: white;

  background: linear-gradient(135deg, #2887F7, #5aa0ff);

  cursor: pointer;
  transition: 0.2s;
}

.run-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(40,135,247,0.4);
}

.run-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* loader */

.loader {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* close */

.close {
  position: absolute;
  right: 16px;
  top: 16px;

  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.6;
}

.close:hover {
  opacity: 1;
}

.success-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(10px);

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 24px;
  z-index: 20;
}

[data-theme="dark"] .success-overlay {
  background: rgba(20,20,20,0.9);
}

.success-box {
  text-align: center;
  animation: pop 0.3s ease;
}

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;

  background: #2887F7;
  color: white;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 28px;
  margin: 0 auto 14px;
}

.success-title {
  font-family: "Eastman-Bold";
  font-size: 18px;
}

.success-subtitle {
  margin-top: 6px;
  font-size: 13px;
  opacity: 0.7;
}

.success-btn {
  margin-top: 16px;
  padding: 12px 20px;

  border: none;
  border-radius: 12px;

  background: #2887F7;
  color: white;

  font-family: "Eastman-Bold";
  cursor: pointer;
}

.success-btn:hover {
  transform: translateY(-1px);
}

/* animation */

@keyframes pop {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.success-overlay {
pointer-events: all;
}

@media (max-width: 768px) {
  .modal-overlay {
    align-items: flex-end;
    justify-content: center;
    padding: 0;
  }

  .modal {
    width: 100%;
    max-width: none;
    padding: 22px 18px calc(22px + env(safe-area-inset-bottom));
    border-radius: 28px 28px 0 0;
    box-sizing: border-box;
  }

  .header h2 {
    font-size: 16px;
    padding-right: 32px;
  }

  .subtitle {
    font-size: 12px;
    line-height: 1.4;
    margin-top: 10px;
  }

  .upload-zone {
    margin-top: 18px;
    padding: 26px 18px;
    border-radius: 20px;
  }

  .upload-icon {
    font-size: 30px;
  }

  .upload-title {
    font-size: 14px;
  }

  .upload-sub {
    font-size: 12px;
  }

  .file-chip {
    max-width: 100%;
    box-sizing: border-box;
  }

  .file-chip span {
    max-width: 230px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .run-btn {
    height: 50px;
    border-radius: 999px;
    font-size: 14px;
  }

  .close {
    right: 18px;
    top: 18px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(0,0,0,0.06);
  }

  [data-theme="dark"] .close {
    background: rgba(255,255,255,0.08);
    color: white;
  }

  .success-overlay {
    border-radius: 28px 28px 0 0;
  }

  .success-box {
    padding: 0 18px;
  }

  .success-title {
    font-size: 17px;
  }

  .success-subtitle {
    font-size: 12px;
    line-height: 1.4;
  }

  .success-btn {
    width: 100%;
    max-width: 240px;
    height: 46px;
    border-radius: 999px;
  }
}

@media (max-width: 430px) {
  .modal {
    padding-left: 16px;
    padding-right: 16px;
  }

  .file-chip span {
    max-width: 210px;
  }
}

.file-chip {
  width: 100%;
  max-width: 100%;

  display: flex;
  align-items: flex-start;
  gap: 8px;

  padding: 10px 12px;
  box-sizing: border-box;

  border-radius: 16px;

  background: #2887f7;
  color: white;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.4;

  text-align: left;
}

.file-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.file-name {
  min-width: 0;
  flex: 1;

  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.file-remove {
  width: 22px;
  height: 22px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;
  border-radius: 50%;

  background: rgba(255, 255, 255, 0.12);
  color: white;

  cursor: pointer;

  transition: background-color 0.2s ease;
}

.file-remove:hover {
  background: rgba(255, 255, 255, 0.24);
}

</style>